import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MailCheck, Play, RefreshCw, Loader2, Search, CalendarClock } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const JOB_LABEL = { "relances-candidatures": "Relances candidatures", "retards-projets": "Retards projets (lundi)", "relance-directe": "Relance directe (utilisateur)" };
const JOB_SCHEDULE = { "relances-candidatures": "Tous les jours à 08:00 UTC", "retards-projets": "Chaque lundi à 07:00 UTC", "relance-directe": "À la demande, depuis le suivi des candidatures" };
const STATUS = {
  sent: ["Envoyé", "bg-emerald-100 text-emerald-700"], failed: ["Échec", "bg-red-100 text-red-700"],
  skipped_pref: ["Désactivé par l'utilisateur", "bg-slate-100 text-slate-600"], no_email: ["Sans adresse", "bg-amber-100 text-amber-700"],
};
const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

export function CronJournal() {
  const [runs, setRuns] = useState([]);
  const [emails, setEmails] = useState([]);
  const [summary, setSummary] = useState({});
  const [job, setJob] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([api.get("/admin/cron/logs", { params: { job, limit: 60 } }), api.get("/admin/cron/emails", { params: { job, status, q, limit: 200 } })]);
      setRuns(r.data.logs || []); setEmails(e.data.emails || []); setSummary(e.data.summary_30d || {});
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [job, status, q]); // eslint-disable-line

  const runNow = async (j) => {
    setRunning(j);
    try { await api.post(`/admin/cron/run/${j}`); toast.success(`${JOB_LABEL[j]} : exécution lancée`); setTimeout(load, 6000); }
    catch (e) { toast.error(apiError(e)); } finally { setTimeout(() => setRunning(""), 6000); }
  };

  return (
    <div data-testid="cron-journal">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-ciel">Tâches planifiées & emails</p>
          <h2 className="font-display text-xl font-bold text-navy">Journal des envois</h2>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="rounded-full" data-testid="cron-refresh"><RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualiser</Button>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-3" data-testid="cron-summary">
        {Object.keys(JOB_LABEL).map((j) => {
          const s = summary[j] || {};
          return (
            <div key={j} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`cron-card-${j}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-navy">{JOB_LABEL[j]}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400"><CalendarClock size={11} /> {JOB_SCHEDULE[j]}</p>
                </div>
                {j !== "relance-directe" && (
                  <Button onClick={() => runNow(j)} disabled={running === j} size="sm" className="h-7 rounded-full bg-[#0E7C7B] px-2.5 text-[11px] text-white hover:bg-[#0c6a69]" data-testid={`cron-run-${j}`}>
                    {running === j ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Play size={11} className="mr-1" />} Lancer maintenant
                  </Button>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                {Object.entries(STATUS).map(([k, [l, c]]) => <span key={k} className={`rounded-full px-2 py-0.5 font-semibold ${c}`} data-testid={`cron-count-${j}-${k}`}>{l} · {s[k] || 0}</span>)}
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">30 derniers jours</p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="cron-runs">
        <p className="font-semibold text-navy">Exécutions ({runs.length})</p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-xs">
            <thead><tr className="border-b border-slate-200 text-slate-500">{["Date", "Tâche", "Identifiant", "Destinataires notifiés", "Éléments", "Ignorés"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {runs.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Aucune exécution enregistrée.</td></tr> : runs.map((r, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`cron-run-row-${i}`}>
                  <td className="px-3 py-2 text-slate-600">{fmt(r.at)}</td>
                  <td className="px-3 py-2 font-medium text-navy">{JOB_LABEL[r.job] || r.job}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-400">{r.run_id}</td>
                  <td className="px-3 py-2 font-semibold text-emerald-700">{r.users_notified ?? 0}</td>
                  <td className="px-3 py-2 text-slate-600">{r.applications ?? r.projects ?? 0}</td>
                  <td className="px-3 py-2 text-slate-600">{r.skipped ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="cron-emails">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 font-semibold text-navy"><MailCheck size={16} className="text-[#0E7C7B]" /> Emails ({emails.length})</p>
          <div className="flex flex-wrap gap-2">
            <select value={job} onChange={(e) => setJob(e.target.value)} data-testid="cron-filter-job" className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-navy">
              <option value="">Toutes les tâches</option>{Object.keys(JOB_LABEL).map((j) => <option key={j} value={j}>{JOB_LABEL[j]}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cron-filter-status" className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-navy">
              <option value="">Tous les statuts</option>{Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <div className="relative"><Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Destinataire, objet…" data-testid="cron-filter-q" className="h-8 w-56 pl-8 text-xs" /></div>
          </div>
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-xs">
            <thead><tr className="border-b border-slate-200 text-slate-500">{["Date", "Tâche", "Destinataire", "Objet", "Éléments", "Statut"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {emails.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Aucun email enregistré.</td></tr> : emails.map((e, i) => {
                const [l, c] = STATUS[e.status] || [e.status, "bg-slate-100 text-slate-600"];
                return (
                  <tr key={i} className="border-b border-slate-100 align-top" data-testid={`cron-email-row-${i}`}>
                    <td className="px-3 py-2 text-slate-600">{fmt(e.at)}</td>
                    <td className="px-3 py-2 text-navy">{JOB_LABEL[e.job] || e.job}</td>
                    <td className="px-3 py-2 text-slate-700">{e.to || "—"}</td>
                    <td className="max-w-[320px] truncate px-3 py-2 text-slate-600" title={e.subject}>{e.subject}</td>
                    <td className="px-3 py-2 text-slate-600">{e.items}</td>
                    <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${c}`} title={e.detail || ""} data-testid={`cron-email-status-${i}`}>{l}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
